import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  BoltIcon,
  MapPinIcon,
  SparklesIcon,
  UserGroupIcon,
  LockOpenIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { poolSearch, poolStats, revealContact, getCandidateMatches, applyToInternalJob, requestIntro } from '../services/api';
import { track, EVENTS } from '../services/analytics';

/* ----------------------------------------------------------------------------
 * Pool Search — the paid recruiter side of the two-sided model.
 * Search a pool of motivated, intent-scored candidates ("backend engineers,
 * open now, SF/remote"). Results are PII-safe (names masked; contact reveal is a
 * separate action) — the differentiator vs. scraped-profile tools: live intent.
 * -------------------------------------------------------------------------- */

const SENIORITY = ['', 'junior', 'mid', 'senior', 'staff', 'lead'];
const REMOTE = ['', 'remote', 'hybrid', 'onsite', 'flexible'];

const intentColor = (s) =>
  s >= 75 ? 'bg-green-100 text-green-800 ring-green-600/20'
  : s >= 50 ? 'bg-blue-100 text-blue-800 ring-blue-600/20'
  : s >= 25 ? 'bg-amber-100 text-amber-800 ring-amber-600/20'
  : 'bg-gray-100 text-gray-600 ring-gray-500/20';

const availabilityBadge = (a) => {
  if (a === 'open_now') return { label: 'Open now', cls: 'bg-green-50 text-green-700 ring-green-600/20' };
  if (a === 'open_casual') return { label: 'Casually open', cls: 'bg-blue-50 text-blue-700 ring-blue-600/20' };
  if (a === 'not_looking') return { label: 'Not looking', cls: 'bg-gray-50 text-gray-500 ring-gray-400/20' };
  return { label: 'Unknown', cls: 'bg-gray-50 text-gray-500 ring-gray-400/20' };
};

const sinceLabel = (ts) => {
  if (!ts) return 'inactive';
  const days = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
  if (days <= 0) return 'active today';
  if (days === 1) return 'active yesterday';
  if (days < 7) return `active ${days}d ago`;
  if (days < 30) return `active ${Math.floor(days / 7)}w ago`;
  return `active ${Math.floor(days / 30)}mo ago`;
};

function Pool() {
  const [filters, setFilters] = useState({
    query: '', seniority: '', location: '', remote: '', minIntent: 0, openNow: false,
  });
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [stats, setStats] = useState({ poolSize: 0, openNow: 0 });
  // Per-candidate reveal state: { [id]: { loading?, contact? } }
  const [reveals, setReveals] = useState({});
  // Per-candidate internal-match state: { [id]: { loading?, matches? } }
  const [matches, setMatches] = useState({});

  const handleMatches = async (id) => {
    if (matches[id]?.matches) { setMatches((m) => ({ ...m, [id]: { ...m[id], open: !m[id].open } })); return; }
    setMatches((m) => ({ ...m, [id]: { loading: true, open: true } }));
    try {
      const res = await getCandidateMatches(id);
      setMatches((m) => ({ ...m, [id]: { loading: false, open: true, matches: res.data.matches || [] } }));
      track(EVENTS.AGENT_MATCH, { count: res.data.matches?.length || 0 });
    } catch (e) {
      setMatches((m) => ({ ...m, [id]: { loading: false, open: false } }));
      toast.error('Could not load matches');
    }
  };

  const handleApply = async (candidateId, jobId) => {
    try {
      await applyToInternalJob(candidateId, jobId);
      toast.success('Applied to the role');
      track(EVENTS.AGENT_APPLY, { jobId });
    } catch (e) {
      // Anti-spam gate: low_fit (422) or apply_limit (429) come back with a message.
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Could not apply');
    }
  };

  const handleIntro = async (id) => {
    try {
      await requestIntro(id, null, "I'd like to connect about a role.");
      toast.success('Intro requested — the candidate will be asked to accept');
    } catch (e) {
      if (e.response?.status === 429) toast.error(e.response.data?.message || 'Daily intro limit reached');
      else toast.error(e.response?.data?.error || 'Could not request intro');
    }
  };

  const handleReveal = async (id) => {
    setReveals((r) => ({ ...r, [id]: { ...r[id], loading: true } }));
    try {
      const res = await revealContact(id);
      setReveals((r) => ({ ...r, [id]: { loading: false, contact: res.data.contact } }));
      track(EVENTS.CONTACT_REVEAL, {});
    } catch (e) {
      setReveals((r) => ({ ...r, [id]: { ...r[id], loading: false } }));
      if (e.response?.status === 402) {
        track(EVENTS.REVEAL_LIMIT, {});
        toast.error(
          <span>
            Monthly reveal limit reached —{' '}
            <Link to="/pricing" className="font-semibold underline">upgrade to reveal more</Link>
          </span>
        );
      } else {
        toast.error(e.response?.data?.error || 'Could not reveal contact');
      }
    }
  };

  const runSearch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await poolSearch(filters);
      setCandidates(res.data.candidates || []);
      setSearched(true);
      track(EVENTS.POOL_SEARCH, { openNow: filters.openNow, minIntent: filters.minIntent });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Pool search failed');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    poolStats().then((r) => setStats(r.data)).catch(() => {});
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="mb-1 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.08em] text-gray-500">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
              Recruiter · Talent Pool
            </div>
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-gray-900">
              Candidates who want your role
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Skip the 300-application flood — start with motivated, intent-scored talent.
            </p>
          </div>
          <div className="flex gap-3">
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-center shadow-sm">
              <div className="flex items-center gap-1.5 text-lg font-semibold text-gray-900">
                <UserGroupIcon className="h-4 w-4 text-blue-600" />{stats.poolSize}
              </div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500">in pool</div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-center shadow-sm">
              <div className="flex items-center gap-1.5 text-lg font-semibold text-green-700">
                <BoltIcon className="h-4 w-4 text-green-600" />{stats.openNow}
              </div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500">open now</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="lg:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600">Role or skill</label>
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  value={filters.query}
                  onChange={(e) => update('query', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                  placeholder="e.g. backend engineer, React, ML"
                  className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Seniority</label>
              <select
                value={filters.seniority}
                onChange={(e) => update('seniority', e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 px-3 text-sm capitalize focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                {SENIORITY.map((s) => <option key={s} value={s}>{s || 'Any'}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Location</label>
              <input
                value={filters.location}
                onChange={(e) => update('location', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                placeholder="e.g. SF, NYC, remote"
                className="w-full rounded-lg border border-gray-300 py-2 px-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Remote</label>
              <select
                value={filters.remote}
                onChange={(e) => update('remote', e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 px-3 text-sm capitalize focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                {REMOTE.map((r) => <option key={r} value={r}>{r || 'Any'}</option>)}
              </select>
            </div>
            <div className="lg:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Min intent score: <span className="font-semibold text-blue-700">{filters.minIntent}</span>
              </label>
              <input
                type="range" min="0" max="100" step="5"
                value={filters.minIntent}
                onChange={(e) => update('minIntent', Number(e.target.value))}
                className="mt-2 w-full accent-blue-600"
              />
            </div>
            <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filters.openNow}
                onChange={(e) => update('openNow', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Open now only
            </label>
            <button
              onClick={runSearch}
              disabled={loading}
              className="self-end rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? 'Searching…' : 'Search pool'}
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="mt-6">
          {loading ? (
            <div className="py-16 text-center text-sm text-gray-500">Searching the pool…</div>
          ) : candidates.length === 0 && searched ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-16 text-center">
              <SparklesIcon className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-700">No matching candidates yet</p>
              <p className="mt-1 text-sm text-gray-500">Loosen the filters, or grow the pool via the free candidate agent.</p>
            </div>
          ) : (
            <>
              <p className="mb-3 text-sm text-gray-500">{candidates.length} candidate{candidates.length === 1 ? '' : 's'}, ranked by intent</p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {candidates.map((c) => {
                  const avail = availabilityBadge(c.availability);
                  return (
                    <div key={c.id} className="flex gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md">
                      <div className="flex flex-col items-center justify-center">
                        <div className={`flex h-14 w-14 items-center justify-center rounded-xl text-lg font-bold ring-1 ${intentColor(c.intent_score)}`}>
                          {c.intent_score}
                        </div>
                        <div className="mt-1 text-[10px] uppercase tracking-wide text-gray-400">intent</div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="truncate font-semibold text-gray-900">{c.display_name}</h3>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${avail.cls}`}>{avail.label}</span>
                        </div>
                        {c.desired_roles && <p className="mt-0.5 truncate text-sm text-gray-700">{c.desired_roles}</p>}
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                          {c.location && <span className="inline-flex items-center gap-1"><MapPinIcon className="h-3.5 w-3.5" />{c.location}</span>}
                          {c.target_seniority && <span className="capitalize">{c.target_seniority}</span>}
                          {c.remote_preference && <span className="capitalize">{c.remote_preference}</span>}
                          <span>{sinceLabel(c.last_active_at)}</span>
                        </div>
                        {Array.isArray(c.skills) && c.skills.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {c.skills.slice(0, 6).map((s, i) => (
                              <span key={i} className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">{s}</span>
                            ))}
                          </div>
                        )}

                        {/* Contact reveal (paid / quota-gated) */}
                        <div className="mt-3 border-t border-gray-100 pt-3">
                          {reveals[c.id]?.contact ? (
                            <div className="space-y-1 text-sm">
                              <div className="font-semibold text-gray-900">{reveals[c.id].contact.full_name}</div>
                              {reveals[c.id].contact.email && (
                                <a
                                  href={`mailto:${reveals[c.id].contact.email}`}
                                  className="inline-flex items-center gap-1.5 text-blue-700 hover:underline"
                                >
                                  <EnvelopeIcon className="h-3.5 w-3.5" />{reveals[c.id].contact.email}
                                </a>
                              )}
                              {reveals[c.id].contact.phone && (
                                <div className="flex items-center gap-1.5 text-gray-600">
                                  <PhoneIcon className="h-3.5 w-3.5" />{reveals[c.id].contact.phone}
                                </div>
                              )}
                              {reveals[c.id].contact.linkedin_url && (
                                <a
                                  href={reveals[c.id].contact.linkedin_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1.5 text-blue-700 hover:underline"
                                >
                                  LinkedIn
                                </a>
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={() => handleReveal(c.id)}
                              disabled={reveals[c.id]?.loading}
                              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
                            >
                              <LockOpenIcon className="h-3.5 w-3.5" />
                              {reveals[c.id]?.loading ? 'Revealing…' : 'Reveal contact'}
                            </button>
                          )}
                          <button
                            onClick={() => handleMatches(c.id)}
                            className="ml-2 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                          >
                            <SparklesIcon className="h-3.5 w-3.5 text-blue-600" />
                            {matches[c.id]?.loading ? 'Matching…' : 'Matched roles'}
                          </button>
                          <button
                            onClick={() => handleIntro(c.id)}
                            className="ml-2 inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
                            title="Double opt-in: the candidate is asked to accept before any intro"
                          >
                            Request intro
                          </button>

                          {matches[c.id]?.open && matches[c.id]?.matches && (
                            <div className="mt-2 space-y-1.5">
                              {matches[c.id].matches.length === 0 && (
                                <p className="text-xs text-gray-500">No internal roles match yet.</p>
                              )}
                              {matches[c.id].matches.slice(0, 4).map((m) => (
                                <div key={m.job_id} className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-2.5 py-1.5">
                                  <div className="min-w-0">
                                    <div className="truncate text-xs font-medium text-gray-800">{m.title} · {m.company}</div>
                                    <div className="text-[11px] text-gray-500">{m.location} · fit {m.fit_score}</div>
                                  </div>
                                  <button
                                    onClick={() => handleApply(c.id, m.job_id)}
                                    className="shrink-0 rounded-md bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-blue-700"
                                  >
                                    Apply
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Pool;
